import type { EditorGameAreaView } from 'editor/EditorGameAreaView';
import type { TileStackModel } from 'game/boardMVC/models/board/TileStackModel';
import { isConnectedBlocker } from 'game/boardMVC/utils/isConnectedBlocker';
import * as HTMLElementCreator from '../../../libs/HTML/HTMLElementCreator';
import type { EditorPanel, EditorPanelStateChangeCallback } from './types';
import {
  removeConnections,
  createConnections,
  validateMeasureTapeConnectionConditions,
} from '../../utils/EditorBlockerConnectionUtils';

interface ButtonConfig {
  action: Action;
  color: string;
  borderColor: string;
  image?: string;
}

enum Action {
  NavigateBack,
  AddConnection,
  RemoveConnection,
}

const buttonsConfigs: ButtonConfig[] = [
  {
    action: Action.NavigateBack,
    color: '#000000',
    borderColor: '#ffffff',
    image: './assets/images/editor/back.jpg',
  },
  {
    action: Action.RemoveConnection,
    color: '#ff0000',
    borderColor: '#ffffff',
    image: './assets/images/editor/red-x.jpg',
  },
  {
    action: Action.AddConnection,
    color: '#00ff00',
    borderColor: '#ffffff',
    image: './assets/images/editor/green-plus.jpg',
  },
];

interface EditorConnectionSelectionConfig {
  readonly onConnectionButtonSelected?: (action: Action) => void;
}

interface Button {
  element: HTMLElement;
  config: ButtonConfig;
}

export class EditorBlockerConnectionsPanel implements EditorPanel {
  private static readonly buttonSize = '40px';

  public readonly element: HTMLElement;

  private readonly buttons: Button[];

  private selectedAction: Action = Action.AddConnection;

  private currentStack: TileStackModel | null = null;

  private previousStack: TileStackModel | null = null;

  constructor(
    private readonly onStateUpdate: EditorPanelStateChangeCallback,
    private readonly getGameAreaView: () => EditorGameAreaView | undefined,
    private readonly onExitPanel: () => void,
    private readonly config: EditorConnectionSelectionConfig = {},
  ) {
    this.element = HTMLElementCreator.createDiv({
      elementID: 'editor-color-panel',
      parentElement: undefined,
      style: {
        position: 'fixed',
        top: '50px',
        right: '5px',
        padding: '2px 2px 2px 2px',
        color: 'black',
        maxWidth: EditorBlockerConnectionsPanel.buttonSize,
        pointerEvents: 'auto',
        backgroundColor: 'black',
      },
    });

    this.buttons = buttonsConfigs.map((buttonConfig) => this.createButton(buttonConfig));

    this.updateButtonHighlights();
  }

  public destroy(): void {
    this.element.remove();
  }

  public focus(): void {
    this.element.style.visibility = 'visible';
  }

  public blur(): void {
    this.element.style.visibility = 'hidden';
    if (this.currentStack) {
      this.getGameAreaView()?.setStackHighlight(this.currentStack.id);
      this.currentStack = null;
      this.previousStack = null;
    }
  }

  public edit(stackModel: TileStackModel): void {
    const editorAreaView = this.getGameAreaView();
    const boardModel = editorAreaView?.boardView?.getModel();

    if (!editorAreaView || !boardModel) return;

    this.currentStack = stackModel;

    if (this.currentStack.blocker && isConnectedBlocker(this.currentStack.blocker)) {
      editorAreaView.setStackHighlight(this.currentStack.id);

      if (this.selectedAction === Action.RemoveConnection) {
        removeConnections(boardModel, editorAreaView, this.currentStack);
        this.onStateUpdate({ board: boardModel.exportState() });
      }

      if (this.selectedAction === Action.AddConnection) {
        if (this.previousStack?.blocker && isConnectedBlocker(this.previousStack.blocker)) {
          const alreadyConnected =
            this.currentStack.blocker.connection.isConnectedTo(this.previousStack) &&
            this.previousStack.blocker?.connection.isConnectedTo(this.currentStack);

          const blockerTypesEqual = this.previousStack.blocker?.getType() === this.currentStack.blocker?.getType();
          const isNeighborOf = this.currentStack.isNeighborOf(this.previousStack);

          if (
            !alreadyConnected &&
            blockerTypesEqual &&
            isNeighborOf &&
            validateMeasureTapeConnectionConditions(this.currentStack, this.previousStack)
          ) {
            createConnections(editorAreaView, this.currentStack, this.previousStack);
            this.onStateUpdate({ board: boardModel.exportState() });
          }
        }
      }
    }

    this.previousStack = this.currentStack;
  }

  private createButton(config: ButtonConfig): Button {
    const { action, color, image } = config;
    const element = HTMLElementCreator.createButton({
      parentElement: this.element,
      labelText: '',
      onClick: () => {
        if (action === Action.NavigateBack) {
          this.onExitPanel();
          return;
        }

        this.onConnectionButtonSelected(action);
      },
      style: {
        backgroundColor: color,
        height: EditorBlockerConnectionsPanel.buttonSize,
        width: EditorBlockerConnectionsPanel.buttonSize,
        borderWidth: '0px',
        borderStyle: 'dashed',
        borderColor: config.borderColor,

        color: config.borderColor,
        userSelect: 'none',
        marginTop: '2px',
        padding: '3px 3px 3px 3px',
      },
    });
    if (config.image) {
      element.style.backgroundImage = `url(${image})`;
      element.style.backgroundSize = 'contain';
    }
    return { element, config };
  }

  private onConnectionButtonSelected(action: Action): void {
    this.selectedAction = action;
    this.updateButtonHighlights();

    if (this.currentStack) {
      this.getGameAreaView()?.setStackHighlight(this.currentStack.id);
      this.currentStack = null;
    }
    this.previousStack = null;

    if (this.config.onConnectionButtonSelected) {
      this.config.onConnectionButtonSelected(this.selectedAction);
    }
  }

  private updateButtonHighlights(): void {
    this.buttons.forEach(({ element, config }) => {
      if (config.action === this.selectedAction) {
        element.style.borderWidth = '2px';
        element.innerHTML = '+';
      } else {
        element.style.borderWidth = '0px';
        element.innerHTML = '';
      }
    });
  }
}
